import { useCallback, useMemo } from 'react';
import { UNSEATED_GROUP_ID } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/constants';
import { useTableSeatingStore } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/store';

const useTableOperations = (tableId: string) => {
    const {
        table,
        tickets,
        seatingGroups,
        updateTableCapacity,
        removeSeatingGroup,
        removeTableSeat,
        takeSnapshot,
    } = useTableSeatingStore(state => ({
        table: state.seatingGroups[tableId],
        tickets: state.tickets,
        seatingGroups: state.seatingGroups,
        updateTableCapacity: state.updateTableCapacity,
        removeSeatingGroup: state.removeSeatingGroup,
        removeTableSeat: state.removeTableSeat,
        takeSnapshot: state.takeSnapshot,
    }));

    const tableTickets = useMemo(() => {
        if (!tableId || !tickets) return [];
        return Object.values(tickets)
            .filter(t => t.seating_group_id === tableId)
            .sort((a, b) => (a.seat_number || 0) - (b.seat_number || 0));
    }, [tickets, tableId]);

    const occupiedSeatsCount = tableTickets.length;

    const occupancyData = useMemo(() => {
        if (!table) return null;
        return {
            occupied: occupiedSeatsCount,
            capacity: table.capacity || 0,
            percentage: table.capacity ? (occupiedSeatsCount / table.capacity) * 100 : 0,
            tickets: tableTickets,
        };
    }, [table, tableTickets, occupiedSeatsCount]);

    const canAddSeat = Boolean(table?.capacity !== null);

    const canRemoveSeat = Boolean(
        table?.capacity !== null &&
            table.capacity > 1 &&
            occupancyData &&
            table.capacity > occupancyData.occupied,
    );

    const remainingTables = useMemo(() => {
        return Object.values(seatingGroups).filter(
            g => g.id !== UNSEATED_GROUP_ID && g.id !== tableId,
        );
    }, [seatingGroups, tableId]);

    const canRemoveTable = Boolean(
        table?.id !== 'unseated' && occupancyData?.occupied === 0 && remainingTables.length > 0,
    );

    const addSeat = useCallback(() => {
        if (table && canAddSeat) {
            takeSnapshot();
            updateTableCapacity(tableId, table.capacity! + 1);
        }
    }, [table, canAddSeat, updateTableCapacity, tableId, takeSnapshot]);

    const removeSeat = useCallback(() => {
        if (table && canRemoveSeat) {
            takeSnapshot();
            updateTableCapacity(tableId, table.capacity! - 1);
        }
    }, [table, canRemoveSeat, updateTableCapacity, tableId, takeSnapshot]);

    const removeSeatByNumber = useCallback(
        (seatNumber: number) => {
            takeSnapshot();
            removeTableSeat(tableId, seatNumber);
        },
        [removeTableSeat, tableId, takeSnapshot],
    );

    const removeTable = useCallback(() => {
        if (canRemoveTable) {
            takeSnapshot();
            removeSeatingGroup(tableId);
        }
    }, [canRemoveTable, removeSeatingGroup, tableId, takeSnapshot]);

    return {
        table,
        occupancy: occupancyData,
        canAddSeat,
        canRemoveSeat,
        canRemoveTable,
        //
        addSeat,
        removeSeat,
        removeSeatByNumber,
        removeTable,
    };
};

export { useTableOperations };
